/**
 * authorizationUtils.ts
 * ─────────────────────
 * Fonctions utilitaires PURES extraites de DataTypeAuthorizationsView.vue.
 * Sans effet de bord, sans import Vue → 100 % testables en Vitest.
 */
import type {
  ApplicationUserResult,
  GetAuthorizationResult, GetRightsRequestResult,
  User,
} from "@/types/generated/UsedGeneratedTypes";

export interface UserAuthEntry {
  id: string;
  name: string;
  email: string;
  isApplicationManager: boolean;
  isApplicationUser: boolean;
  isUserManager: boolean;
  auth: Array<{ id: string; name: string }>;
}

export interface TableAuthMode {
  hasPublicAuthorizations: boolean;
  hasAuthorizationsByUsers: boolean;
}

export function filterListAuthByName(
  listAuth: GetAuthorizationResult[],
  searchTerm: string,
): GetAuthorizationResult[] {
  return listAuth.filter(auth =>
    auth.name.toString().toLowerCase().includes(searchTerm.toString().toLowerCase()),
  );
}

export function filterListUserByName(
  listUser: User[],
  searchTerm: string,
): User[] {
  return listUser.filter(user =>
    user.label.toString().toLowerCase().includes(searchTerm.toLowerCase()),
  );
}

export function buildUserAuthorizationsMap(
  allUsers: ApplicationUserResult[],
  listAuthorizations: GetAuthorizationResult[],
): Record<string, UserAuthEntry> {
  const result: Record<string, UserAuthEntry> = {};
  allUsers.forEach((user) => {
    listAuthorizations.forEach((auth) => {
      if (!result[user.id] && user.label !== "_public_" && user.isApplicationUser) {
        result[user.id] = {
          id: user.id,
          name: user.label,
          email: user.email,
          isApplicationManager: user.isApplicationManager,
          isApplicationUser: user.isApplicationUser,
          isUserManager: user.isUserManager,
          auth: [],
        };
      }
      auth.users.forEach((authUser) => {
        if (authUser.id === user.id && result[authUser.id]) {
          const authExists = result[authUser.id].auth.some(a => a.id === auth.uuid);
          if (!authExists) {
            result[authUser.id].auth.push({ id: auth.uuid, name: auth.name });
          }
        }
      });
    });
  });
  return result;
}

export function computeTableAuthMode(showTableBy: string): TableAuthMode {
  if (showTableBy === "1") return { hasPublicAuthorizations: false, hasAuthorizationsByUsers: true };
  if (showTableBy === "3") return { hasPublicAuthorizations: true, hasAuthorizationsByUsers: false };
  return { hasPublicAuthorizations: false, hasAuthorizationsByUsers: false };
}

export function filterUsersByAuthPresence(
  users: UserAuthEntry[],
  mode: "userWithAuth" | "userWithoutAuth" | "allUsers",
): UserAuthEntry[] {
  if (mode === "userWithAuth") return users.filter(u => u.auth.length !== 0);
  if (mode === "userWithoutAuth") return users.filter(u => u.auth.length === 0);
  return users;
}
